/**
 * TVFullscreenOverlay Component
 * Phase D2.2: Immersive fullscreen TV viewing experience
 */

import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { X, Volume2, VolumeX, ChevronUp, ChevronDown } from 'lucide-react';
import { mockTVChannels } from '@/data/mockHotelData';
import { getChannelVideoUrl } from '@/data/tvChannelVideos';

interface TVFullscreenOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  currentChannelIndex: number;
  onNextChannel: () => void;
  onPrevChannel: () => void;
  // Phase D2.2: Controlled mute state
  isMuted?: boolean;
  onMuteToggle?: () => void;
}

export function TVFullscreenOverlay({
  isOpen,
  onClose,
  currentChannelIndex,
  onNextChannel,
  onPrevChannel,
  isMuted = true,
  onMuteToggle,
}: TVFullscreenOverlayProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // React's `muted` prop doesn't reliably sync to the DOM property — set it directly
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const currentChannel = mockTVChannels[currentChannelIndex] || mockTVChannels[0];
  const videoUrl = getChannelVideoUrl(currentChannel?.id || 'ch_hbo');

  // Auto-hide controls after 3 seconds of inactivity
  useEffect(() => {
    if (!isOpen) return;

    const resetTimer = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    resetTimer();

    const handleMouseMove = () => resetTimer();
    const handleKeyDown = () => resetTimer();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isOpen]);

  // Handle keyboard shortcuts in fullscreen
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowUp':
          e.preventDefault();
          onPrevChannel();
          break;
        case 'ArrowDown':
          e.preventDefault();
          onNextChannel();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          onMuteToggle?.();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNextChannel, onPrevChannel, onMuteToggle]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 bg-black"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Video Player */}
          <AnimatePresence mode="wait">
            <motion.video
              key={currentChannel?.id}
              ref={videoRef}
              src={videoUrl}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="absolute inset-0 w-full h-full object-contain bg-black"
              style={{ willChange: 'opacity' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            />
          </AnimatePresence>

          {/* Controls Overlay */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: showControls ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Top gradient */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/60 to-transparent" />

            {/* Bottom gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/80 to-transparent" />

            {/* Close button */}
            <button
              className="pointer-events-auto absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
              onClick={onClose}
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Channel info */}
            <div className="pointer-events-auto absolute bottom-8 left-8">
              <motion.div
                key={currentChannel?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center gap-4"
              >
                <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-3xl">
                  {currentChannel?.logo}
                </div>
                <div>
                  <div className="text-2xl font-display font-semibold text-white">
                    {currentChannel?.name}
                  </div>
                  <div className="text-white/60 mt-1">
                    {currentChannel?.currentProgram}
                  </div>
                  <div className="text-white/40 text-sm mt-0.5">
                    Channel {currentChannel?.number}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Channel navigation */}
            <div className="pointer-events-auto absolute right-8 top-1/2 -translate-y-1/2 flex flex-col gap-3">
              <button
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
                onClick={onPrevChannel}
              >
                <ChevronUp className="w-6 h-6 text-white" />
              </button>
              <button
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
                onClick={onNextChannel}
              >
                <ChevronDown className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Volume control */}
            <button
              className={clsx(
                "pointer-events-auto absolute bottom-8 right-8 p-3 rounded-full backdrop-blur-sm transition-all duration-300",
                isMuted
                  ? "bg-white/10 hover:bg-white/20"
                  : "bg-zaxia-teal/30 hover:bg-zaxia-teal/40"
              )}
              onClick={onMuteToggle}
            >
              {isMuted ? (
                <VolumeX className="w-6 h-6 text-white/70" />
              ) : (
                <Volume2 className="w-6 h-6 text-white" />
              )}
            </button>

            {/* Keyboard hints */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-6 text-white/40 text-sm">
              <span>↑↓ Channels</span>
              <span>M Mute</span>
              <span>ESC Exit</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
